import { useCallback, useEffect, useRef, useState } from 'react';

import { AchadosDoAssistenteResponse, buscarAchadosDoAssistente } from '../../api/radarApi';
import { notify } from '../../shared/feedback/notify';

/** De quanto em quanto tempo a seção reconsulta enquanto a exploração está em curso. */
export const INTERVALO_DE_RECONSULTA_MS = 15_000;
/** Quantas reconsultas antes de a seção parar e pedir para recarregar a página. */
export const MAXIMO_DE_RECONSULTAS = 20;

export interface EstadoDosAchados {
  dados: AchadosDoAssistenteResponse | null;
  carregando: boolean;
  erro: boolean;
  /** A exploração continuou em curso além do limite de reconsultas. */
  esperaEsgotada: boolean;
}

/**
 * A carga dos achados do assistente de um radar (transversal/at-82).
 *
 * ## Por que existe reconsulta
 *
 * O conjunto do dia é gerado uma vez, por quem chegar primeiro (ou pelo cron). Quem abre a tela
 * durante essa geração recebe `EM_ANDAMENTO` — e a seção **não pode ficar vazia** nem mostrar o
 * texto de "não propôs nada": os dois seriam afirmações sobre uma verificação que ainda está
 * acontecendo. Ela mostra o esqueleto, diz que está em análise e volta a perguntar sozinha.
 *
 * ⚠️ A espera é limitada. Passado o teto, a seção diz para recarregar em alguns minutos, em vez
 * de girar para sempre — girar indefinidamente é indistinguível de tela travada.
 */
export function useAchadosDoAssistente(radarId: string): EstadoDosAchados {
  const [dados, setDados] = useState<AchadosDoAssistenteResponse | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [esperaEsgotada, setEsperaEsgotada] = useState(false);

  const tentativas = useRef(0);
  const ativo = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const consultar = useCallback(async () => {
    try {
      const resposta = await buscarAchadosDoAssistente(radarId);
      if (!ativo.current) return;

      setDados(resposta);
      setErro(false);

      if (resposta.exploracao.estado === 'EM_ANDAMENTO') {
        if (tentativas.current >= MAXIMO_DE_RECONSULTAS) {
          setEsperaEsgotada(true);
          return;
        }
        tentativas.current += 1;
        timer.current = setTimeout(() => {
          void consultar();
        }, INTERVALO_DE_RECONSULTA_MS);
      }
    } catch (error) {
      if (!ativo.current) return;
      // Estado seguro E sinal (regra da at-60). Nesta seção o vazio por erro teria a silhueta
      // exata de "o assistente não achou nada" — a leitura errada mais barata de fazer, e a que
      // esta seção existe para não permitir.
      setDados(null);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar os achados do assistente. Os demais números da tela não são afetados.',
      );
    } finally {
      if (ativo.current) setCarregando(false);
    }
  }, [radarId]);

  useEffect(() => {
    ativo.current = true;
    tentativas.current = 0;
    void consultar();

    return () => {
      ativo.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [consultar]);

  return { dados, carregando, erro, esperaEsgotada };
}
